/**
 * useInfiniteScrollDateRange - Calculates the full date range for infinite scroll calendar
 *
 * Instead of true infinite scroll (which react-window doesn't support), we:
 * 1. Pre-calculate full date range (business creation → 2 years future)
 * 2. Generate complete days array upfront (finite but large ~700-1000 days)
 * 3. Use this as the foundation for windowed virtualization
 *
 * Memory impact: ~700 dates = ~35KB - negligible for modern devices
 */

import { useMemo, useCallback } from 'react';
import {
  eachDayOfInterval,
  addYears,
  startOfDay,
  isSameDay,
  differenceInDays,
  isAfter,
  isBefore,
  startOfMonth,
  getMonth,
  getYear
} from 'date-fns';

export interface InfiniteScrollDateRange {
  /** Full array of dates from business creation to +2 years future */
  allDays: Date[];
  /** Business creation date (or fallback) */
  startDate: Date;
  /** 2 years from today */
  endDate: Date;
  /** Total number of days in the range */
  totalDays: number;
  /** Index of today in the allDays array (-1 if not in range) */
  todayIndex: number;
  /** Get the index for a specific date */
  getIndexForDate: (date: Date) => number;
  /** Get the date at a specific index */
  getDateAtIndex: (index: number) => Date | null;
  /** Check if a date is within the valid range */
  isDateInRange: (date: Date) => boolean;
  /** Get indices for a month (for chunk loading) */
  getMonthIndices: (year: number, month: number) => { startIndex: number; endIndex: number } | null;
}

export interface UseInfiniteScrollDateRangeOptions {
  /** Business creation date (from selectedBusinessDetails.created_at) */
  businessCreatedAt: string | Date | null | undefined;
  /** How many years in the future to allow scrolling (default: 2) */
  futureYears?: number;
  /** Fallback start date if business creation date is unavailable */
  fallbackStartDate?: Date;
  /** Whether infinite scroll is enabled */
  enabled?: boolean;
}

/**
 * Hook for calculating the full date range for infinite scroll calendar
 *
 * @example
 * const { allDays, todayIndex, getIndexForDate } = useInfiniteScrollDateRange({
 *   businessCreatedAt: selectedBusiness?.created_at,
 *   enabled: infiniteScrollEnabled
 * });
 */
export function useInfiniteScrollDateRange({
  businessCreatedAt,
  futureYears = 2,
  fallbackStartDate,
  enabled = true
}: UseInfiniteScrollDateRangeOptions): InfiniteScrollDateRange {

  // Parse business creation date
  const startDate = useMemo(() => {
    if (!enabled) {
      // Return today as start if not enabled (will use monthly mode)
      return startOfDay(new Date());
    }

    if (businessCreatedAt) {
      const parsed = typeof businessCreatedAt === 'string'
        ? new Date(businessCreatedAt)
        : businessCreatedAt;

      if (!isNaN(parsed.getTime())) {
        return startOfDay(parsed);
      }
    }

    // Fallback: use provided fallback or 1 year ago
    if (fallbackStartDate) {
      return startOfDay(fallbackStartDate);
    }

    // Default fallback: 1 year ago from today
    const today = new Date();
    return startOfDay(addYears(today, -1));
  }, [businessCreatedAt, fallbackStartDate, enabled]);

  // Calculate end date (2 years from today)
  const endDate = useMemo(() => {
    const today = new Date();
    return startOfDay(addYears(today, futureYears));
  }, [futureYears]);

  // Generate all days in the range
  const allDays = useMemo(() => {
    if (!enabled) {
      return [];
    }

    // Ensure startDate is before endDate
    const effectiveStart = isBefore(startDate, endDate) ? startDate : endDate;
    const effectiveEnd = isAfter(endDate, startDate) ? endDate : startDate;

    return eachDayOfInterval({
      start: effectiveStart,
      end: effectiveEnd
    });
  }, [startDate, endDate, enabled]);

  // Calculate today's index
  const todayIndex = useMemo(() => {
    if (!enabled || allDays.length === 0) {
      return -1;
    }

    const today = startOfDay(new Date());
    return allDays.findIndex(day => isSameDay(day, today));
  }, [allDays, enabled]);

  // Get index for a specific date
  const getIndexForDate = useCallback((date: Date): number => {
    if (!enabled || allDays.length === 0) {
      return -1;
    }

    const normalizedDate = startOfDay(date);

    // Optimization: Use binary search for large arrays
    if (allDays.length > 100) {
      let low = 0;
      let high = allDays.length - 1;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const midDate = allDays[mid];

        if (isSameDay(midDate, normalizedDate)) {
          return mid;
        } else if (isBefore(midDate, normalizedDate)) {
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      return -1; // Not found
    }

    // For smaller arrays, use simple find
    return allDays.findIndex(day => isSameDay(day, normalizedDate));
  }, [allDays, enabled]);

  // Get date at a specific index
  const getDateAtIndex = useCallback((index: number): Date | null => {
    if (!enabled || index < 0 || index >= allDays.length) {
      return null;
    }
    return allDays[index];
  }, [allDays, enabled]);

  // Check if date is in range
  const isDateInRange = useCallback((date: Date): boolean => {
    if (!enabled) {
      return false;
    }

    const normalizedDate = startOfDay(date);
    return (
      (isAfter(normalizedDate, startDate) || isSameDay(normalizedDate, startDate)) &&
      (isBefore(normalizedDate, endDate) || isSameDay(normalizedDate, endDate))
    );
  }, [startDate, endDate, enabled]);

  // Get indices for a specific month (useful for chunk loading)
  const getMonthIndices = useCallback((year: number, month: number): { startIndex: number; endIndex: number } | null => {
    if (!enabled || allDays.length === 0) {
      return null;
    }

    // Find first day of the month
    let startIndex = -1;
    let endIndex = -1;

    for (let i = 0; i < allDays.length; i++) {
      const day = allDays[i];
      const dayMonth = getMonth(day);
      const dayYear = getYear(day);

      if (dayYear === year && dayMonth === month) {
        if (startIndex === -1) {
          startIndex = i;
        }
        endIndex = i;
      } else if (startIndex !== -1) {
        // We've passed the month, stop searching
        break;
      }
    }

    if (startIndex === -1) {
      return null;
    }

    return { startIndex, endIndex };
  }, [allDays, enabled]);

  return {
    allDays,
    startDate,
    endDate,
    totalDays: allDays.length,
    todayIndex,
    getIndexForDate,
    getDateAtIndex,
    isDateInRange,
    getMonthIndices
  };
}

/**
 * Helper hook to get month key from date (for chunk tracking)
 */
export function getMonthKey(date: Date): string {
  return `${getYear(date)}-${String(getMonth(date) + 1).padStart(2, '0')}`;
}

/**
 * Helper to parse month key back to year/month
 */
export function parseMonthKey(key: string): { year: number; month: number } {
  const [yearStr, monthStr] = key.split('-');
  return {
    year: parseInt(yearStr, 10),
    month: parseInt(monthStr, 10) - 1 // Convert back to 0-indexed
  };
}
